import { ChangesDetection } from '../../../../change-detection';
import { C_GroupedObservationData } from './grouped-observation-data.model';
import { I_GroupedObservationResponse } from '../api/grouped-observation.response';
import { I_GroupedObservationDataResponse } from '../api/grouped-observation-data.response';
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { E_ChangeAction } from '../../../../../enums/change-action.enum';


export class C_GroupObsList extends ChangesDetection<T_GroupObsListChange> {

  private list: Map<string, Map<string, C_GroupedObservation>> = new Map(); // Nivel 1: ID Paciente -> Nivel 2: Mapa de Observaciones

  private cachedSubs = new Map<string, Observable<C_GroupedObservation | undefined>>(); // Cache de Observables para no recrear streams (Memorization), Key: patientId_obsName

  constructor() {
    super();
  }

  getPatientObservations(patientId: string): C_GroupedObservation[] {
    const patientMap = this.list.get(patientId);
    if (!patientMap) return [];
    return Array.from(patientMap.values());
  }

  setGroupedObservation(gObs: I_GroupedObservationResponse) {
    const obsName = gObs.name;
    const patientId = gObs.patientId;
    if (!obsName || !patientId) return;

    let patientMap = this.list.get(patientId);
    if (!patientMap) {
      patientMap = new Map();
      this.list.set(patientId, patientMap);
    }

    if (patientMap.has(obsName)) {
      const currentGroupedObs = patientMap.get(obsName)!;
      currentGroupedObs.initializeFromJson(gObs);
      this.forceUpdateDetection({
        action: E_ChangeAction.Update,
        patientId,
        obsName,
        data: currentGroupedObs
      });
    } else {
      const newGroupedObs = new C_GroupedObservation(gObs);
      patientMap.set(obsName, newGroupedObs);
      this.forceUpdateDetection({
        action: E_ChangeAction.Add,
        patientId,
        obsName,
        data: newGroupedObs,
      });
    }
  }

  setGroupedObservationsBulk(gObsList: (I_GroupedObservationResponse & { localId: symbol })[]) {
    const affected = new Set<string>();

    gObsList.forEach(gObs => {
      const obsName = gObs.name;
      const patientId = gObs.patientId;
      if (!obsName || !patientId) return;

      let patientMap = this.list.get(patientId);
      if (!patientMap) {
        patientMap = new Map();
        this.list.set(patientId, patientMap);
      }

      affected.add(`${patientId}_${obsName}`);

      if (patientMap.has(obsName)) {
        const currentGroupedObs = patientMap.get(obsName)!;
        currentGroupedObs.initializeFromJson(gObs);
      } else {
        const newGroupedObs = new C_GroupedObservation(gObs);
        patientMap.set(obsName, newGroupedObs);
      }
    });

    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
  }

  /**
   * Obtiene el stream reactivo de UNA agrupada específica de UN paciente.
   */
  getLiveGroupedObs(patientId: string, gObsName: string): Observable<C_GroupedObservation | undefined> {
    const cacheKey = `${patientId}_${gObsName}`;
    if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Permite el disparo inicial (startWith)
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.AddBulk) return change.affected.has(cacheKey);
        return change.patientId === patientId && change.obsName === gObsName;
      }),
      map(() => {
        const patientMap = this.list.get(patientId);
        return patientMap ? patientMap.get(gObsName) : undefined;
      }),
      distinctUntilChanged(),
      switchMap(gObs => {
        if (!gObs) return of(undefined);
        // Escucha mutaciones internas de la instancia de observación agrupada
        return gObs.getForcedChanges().pipe(
          startWith(gObs),
          map(() => gObs)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(cacheKey, stream$);
    return stream$;
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }
}

export class C_GroupedObservation extends ChangesDetection {
  /**
   * Nombre con el que se realizo la peticion de observaciones agrupadas
   */
  name?: string;
  /**
   * Nombre del grupo con el que se realizo la peticion de observaciones agrupadas
   */
  group?: string;
  observations: Map<string, GroupedObservationDataList> = new Map();

  constructor(json: I_GroupedObservationResponse) {
    super();
    if (!json) return;
    this.initializeFromJson(json);

  }

  initializeFromJson(json: I_GroupedObservationResponse) {
    if (json.name) this.name = json.name;
    if (json.group) this.group = json.group;
    if (json.observations) {
      this.observations.clear();
      json.observations.forEach((go: I_GroupedObservationDataResponse) => {
        if (!go.name || !go.time) return;
        const goData = new C_GroupedObservationData(go);
        if (!this.observations.has(go.name)) {
          this.observations.set(go.name, new GroupedObservationDataList());
        }
        this.observations.get(go.name)?.set(go.time, goData);
      });
    }
    this.forceUpdateDetection();
  }

}

export class GroupedObservationDataList extends ChangesDetection {
  private list: Map<string, C_GroupedObservationData> = new Map();

  constructor() {
    super();
  }

  getValues(): C_GroupedObservationData[] {
    return Array.from(this.list.values());
  }

  has(time: string): boolean {
    return this.list.has(time);
  }

  set(time: string, data: C_GroupedObservationData): void {
    this.list.set(time, data);
  }

  get(time: string): C_GroupedObservationData | undefined {
    return this.list.get(time);
  }

}

export type T_GroupObsListChange =
  | { action: E_ChangeAction.Add; patientId: string; obsName: string; data: C_GroupedObservation }
  | { action: E_ChangeAction.Update; patientId: string; obsName: string; data: C_GroupedObservation }
  | { action: E_ChangeAction.AddBulk; affected: Set<string> }
  | { action: E_ChangeAction.Reset };
